/**
 * Lettering over the world: name tags, karma and stance badges, statuses, speech bubbles and move
 * callouts, drawn on the screen canvas (the `labels` render pass, render/passes/labels.ts). Titles
 * are drawn in their plates (plates/).
 */
import {
  clamp,
  KARMA_LABELS,
  pkColor,
  plateOf,
  STATUSES,
  titleByName,
  TITLES,
  type Entity,
  type TitleDef,
  type World,
} from '@nine/shared';
import { drawTitlePlateAt } from '../plates';
import { CROWD } from './drawKit';
import { Effects } from './effects';
import { armourBadge } from './passes/duelArmorTell';

export const HAN = '"Noto Serif TC", "Songti TC", serif';
export const UI = 'Mitr, system-ui, sans-serif';
export const PIXEL = '"Pixelify Sans", Mitr, monospace';

/** A player's name colour: orange, red or purple by PK points; else as given (red tint with PK on). */
export function nameColor(e: Entity, plain: string): string {
  const c = pkColor(e.pkPoints);
  if (c === 'purple') return '#c070ff';
  if (c === 'red') return '#ff3a44';
  if (c === 'orange') return '#ffa040';
  return e.pk ? '#ff8a90' : plain;
}

export function tag(
  mg: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  font: string,
  color: string,
): void {
  mg.font = font;
  mg.fillStyle = color;
  (mg as CanvasRenderingContext2D & { outlinedText(text: string, x: number, y: number): void }).outlinedText(
    text,
    x,
    y,
  );
}

/**
 * เถาฉางเยี่ย: black brushed characters of his legend spiralling round the body, rising from
 * the ground into the sky and fading as they go; those behind the body are dimmer.
 */
export function risingGlyphs(
  mg: CanvasRenderingContext2D,
  t: number,
  PX: number,
  fx: Effects,
  e: Entity,
  x: number,
  groundY: number,
): void {
  const foot = groundY - (fx.anim(e.id).elev + e.z + fx.hoverOf(e)) * PX;
  const words = '問劍無悔九劍無敵';
  mg.save();
  mg.textAlign = 'center';
  mg.textBaseline = 'middle';
  for (let i = 0; i < words.length; i++) {
    const ph = (t * 0.22 + i / words.length) % 1;
    const a = t * 1.6 + i * 0.79;
    const r = (16 + ph * 10) * PX;
    const gx = x + Math.cos(a) * r;
    const gy = foot - ph * 70 * PX + Math.sin(a) * r * 0.3;
    const behind = Math.sin(a) < 0;
    const fade = Math.min(1, ph / 0.15, (1 - ph) / 0.3);
    mg.globalAlpha = fade * (behind ? 0.45 : 0.95);
    mg.font = `900 ${Math.round((behind ? 11 : 14) + ph * 4)}px ${HAN}`;
    // A red halo as a wide stroke rather than a blurred shadow (far cheaper every frame).
    mg.lineWidth = 6;
    mg.strokeStyle = 'rgba(200,30,40,.35)';
    mg.strokeText(words[i]!, gx, gy);
    mg.lineWidth = 3;
    mg.strokeStyle = 'rgba(235,235,240,.55)';
    mg.strokeText(words[i]!, gx, gy);
    mg.fillStyle = '#0a0a0e';
    mg.fillText(words[i]!, gx, gy);
  }
  mg.restore();
  mg.lineWidth = 3.5;
  mg.strokeStyle = 'rgba(12,9,14,.92)';
}

/** A small badge left of a player's name: their karma (ดี ปกติ ชั่ว บ้า). */
export function karmaBadge(mg: CanvasRenderingContext2D, e: Entity, x: number, y: number): void {
  const col = { good: '#7ac8ff', normal: '#b8b0a0', evil: '#ff4a50', mad: '#c070ff' }[e.karma];
  mg.save();
  mg.font = `600 11px ${UI}`;
  const nameW = (() => {
    mg.font = `600 14px ${UI}`;
    return mg.measureText(e.name).width;
  })();
  mg.font = `600 11px ${UI}`;
  const label = KARMA_LABELS[e.karma];
  const w = mg.measureText(label).width + 10;
  const bx = x - nameW / 2 - w - 6;
  const by = y - 13;
  mg.fillStyle = 'rgba(10,8,12,.85)';
  mg.fillRect(bx, by, w, 15);
  mg.strokeStyle = col;
  mg.lineWidth = 1;
  mg.strokeRect(bx + 0.5, by + 0.5, w - 1, 14);
  mg.fillStyle = col;
  mg.textAlign = 'center';
  mg.fillText(label, bx + w / 2, by + 11);
  mg.restore();
  mg.lineWidth = 3.5;
  mg.strokeStyle = 'rgba(12,9,14,.92)';
}

export function stanceIcon(mg: CanvasRenderingContext2D, e: Entity, x: number, y: number): void {
  if (!e.blocking && !e.cast) return;
  const isBreak = e.cast?.kind === 'break';
  const glyph = e.blocking ? '守' : isBreak ? '破' : '攻';
  const col = e.blocking ? '#8cc6ff' : isBreak ? '#ffa040' : '#ff6a5a';
  const prog = e.cast ? clamp(e.cast.t / e.cast.windup, 0, 1) : -1;
  const r = 13;
  mg.fillStyle = 'rgba(12,9,14,.85)';
  mg.beginPath();
  mg.arc(x, y, r, 0, Math.PI * 2);
  mg.fill();
  mg.lineWidth = 2;
  mg.strokeStyle = col;
  mg.beginPath();
  if (prog >= 0) mg.arc(x, y, r, -Math.PI / 2, -Math.PI / 2 + prog * Math.PI * 2);
  else mg.arc(x, y, r, 0, Math.PI * 2);
  mg.stroke();
  mg.font = `900 16px ${HAN}`;
  mg.fillStyle = col;
  mg.textBaseline = 'middle';
  mg.fillText(glyph, x, y + 1);
  mg.textBaseline = 'alphabetic';
  mg.lineWidth = 3.5;
  mg.strokeStyle = 'rgba(12,9,14,.92)';
}

/** A move's name in calligraphy beside a red seal with its glyph; pops in, drifts up, fades. */
export function callout(
  mg: CanvasRenderingContext2D,
  px: number,
  n: { x: number; y: number; t: number; life: number; text: string; color: string; glyph?: string },
  ox: number,
  oy: number,
): void {
  const k = n.t / n.life;
  const pop = n.t < 0.15 ? 1.6 - (n.t / 0.15) * 0.6 : 1;
  const x = (n.x + ox) * px;
  const y = (n.y + oy) * px - k * 18;
  mg.save();
  mg.globalAlpha = k > 0.75 ? 1 - (k - 0.75) / 0.25 : 1;
  mg.translate(x, y);
  mg.scale(pop, pop);
  mg.font = `700 24px ${UI}`;
  const tw = mg.measureText(n.text).width;
  const S = 30;
  const left = -(S + 8 + tw) / 2;
  mg.fillStyle = '#b8302a';
  mg.fillRect(left, -S / 2, S, S);
  mg.strokeStyle = '#f6e6c8';
  mg.lineWidth = 2;
  mg.strokeRect(left + 3, -S / 2 + 3, S - 6, S - 6);
  mg.font = `900 20px ${HAN}`;
  mg.textAlign = 'center';
  mg.textBaseline = 'middle';
  mg.fillStyle = '#fbeee0';
  mg.fillText(n.glyph ?? '', left + S / 2, 1);
  mg.textAlign = 'left';
  mg.font = `700 24px ${UI}`;
  mg.lineWidth = 5;
  mg.strokeStyle = 'rgba(12,9,14,.95)';
  mg.strokeText(n.text, left + S + 8, 1);
  mg.fillStyle = n.color;
  mg.fillText(n.text, left + S + 8, 1);
  mg.restore();
  mg.textAlign = 'center';
  mg.textBaseline = 'alphabetic';
  mg.lineWidth = 3.5;
  mg.strokeStyle = 'rgba(12,9,14,.92)';
}

/** A row of status glyphs above an entity's name, buffs green and debuffs red, with stacks. */
export function statusIcons(mg: CanvasRenderingContext2D, e: Entity, x: number, y: number): void {
  if (!e.statuses.length) return;
  const step = 16;
  let cx = x - ((e.statuses.length - 1) * step) / 2;
  mg.textBaseline = 'middle';
  for (const st of e.statuses) {
    const d = STATUSES[st.id];
    if (!d) continue;
    mg.fillStyle = 'rgba(10,8,12,.85)';
    mg.fillRect(cx - 7, y - 7, 14, 14);
    mg.strokeStyle = d.kind === 'buff' ? '#3f9a74' : '#a84438';
    mg.lineWidth = 1;
    mg.strokeRect(cx - 7.5, y - 7.5, 15, 15);
    mg.font = `900 11px ${HAN}`;
    mg.fillStyle = d.kind === 'buff' ? '#9ff0c8' : '#ffb0a0';
    mg.fillText(d.glyph, cx, y + 1);
    if (st.stacks > 1) {
      mg.font = `700 9px ${PIXEL}`;
      mg.fillStyle = '#ffffff';
      mg.fillText(String(st.stacks), cx + 7, y - 6);
    }
    cx += step;
  }
  mg.textBaseline = 'alphabetic';
  mg.lineWidth = 3.5;
  mg.strokeStyle = 'rgba(12,9,14,.92)';
}

/** A speech bubble: pops in, holds, fades; long lines wrap. */
export function bubble(
  mg: CanvasRenderingContext2D,
  text: string,
  t: number,
  x: number,
  bottom: number,
): void {
  const life = Effects.BUBBLE_LIFE;
  const a = t < 0.12 ? t / 0.12 : t > life - 0.4 ? (life - t) / 0.4 : 1;
  if (a <= 0) return;
  mg.save();
  mg.globalAlpha = a;
  mg.font = `500 12px ${UI}`;
  // Wrap at about 190px, by words where there are spaces, otherwise by characters.
  const lines: string[] = [];
  let cur = '';
  for (const ch of [...text]) {
    if (mg.measureText(cur + ch).width > 190 && cur) {
      const cut = cur.lastIndexOf(' ');
      if (cut > 0) {
        lines.push(cur.slice(0, cut));
        cur = cur.slice(cut + 1) + ch;
      } else {
        lines.push(cur);
        cur = ch;
      }
    } else cur += ch;
  }
  if (cur) lines.push(cur);
  const lh = 16;
  const w = Math.max(...lines.map((l) => mg.measureText(l).width)) + 16;
  const h = lines.length * lh + 8;
  const pop = t < 0.12 ? 4 * (1 - t / 0.12) : 0;
  const bx = Math.round(x - w / 2);
  const by = Math.round(bottom - h - 6 + pop);
  mg.fillStyle = 'rgba(250,246,236,0.96)';
  mg.strokeStyle = 'rgba(40,30,24,0.85)';
  mg.lineWidth = 1.5;
  mg.beginPath();
  mg.roundRect(bx, by, w, h, 6);
  mg.moveTo(x - 5, by + h);
  mg.lineTo(x, by + h + 6);
  mg.lineTo(x + 5, by + h);
  mg.fill();
  mg.stroke();
  // Cover the outline where the tail joins the box.
  mg.fillRect(x - 4, by + h - 1.5, 8, 2);
  mg.fillStyle = '#2a2018';
  mg.textAlign = 'center';
  mg.textBaseline = 'top';
  lines.forEach((l, k) => mg.fillText(l, x, by + 5 + k * lh));
  mg.restore();
}

/** CSS pixels between a title plate's frame and the top of its bearer's name. */
export const TITLE_PLATE_GAP = 20;

/**
 * A player's labels (the local player or another, `rival`): the title in its plate (the worn
 * title; another player's while the crowd is thin, `crowd`, or they are the target), else as plain
 * text; the name, the karma badge and the stance icon above the plate. Both labels passes draw them
 * with this (render/passes/labels.ts, render3d/overlay/labels.ts); `top` is the head's top on the
 * screen, `clock` the plates' animation time (plates/motion.ts `plateClock`).
 */
export function personLabels(
  mg: CanvasRenderingContext2D,
  w: World,
  e: Entity,
  x: number,
  top: number,
  crowd: number,
  clock: number,
): void {
  let def: TitleDef | undefined;
  if (e.kind === 'player') {
    const worn = TITLES[w.progress.titleWorn];
    def = worn && worn.name === e.title ? worn : undefined;
  } else {
    const own = titleByName(e.title);
    def = own && (crowd < CROWD || e.id === w.player.targetId) ? own : undefined;
  }
  // A bout's fighters keep their title as plain text: the tall plate sits behind the bout HUD on short screens.
  const inBout = w.pvpBout != null && (w.pvpBout.a === e.id || w.pvpBout.b === e.id);
  const plate = def && !inBout
    ? drawTitlePlateAt(mg, x, top - TITLE_PLATE_GAP, { plate: plateOf(def), text: e.title, t: clock })
    : null;
  if (!plate) tag(mg, e.title, x, top - 18, `500 12px ${UI}`, '#f3d27a');
  const plain = e.kind === 'player' ? '#dff5e6' : w.inParty(e) ? '#8ff0a8' : '#bfe8ff';
  tag(mg, e.name, x, top - 2, `600 14px ${UI}`, nameColor(e, plain));
  karmaBadge(mg, e, x, top - 2);
  if (w.pkProtectedEntity(e)) {
    // Newbie protection mark (H26), right of the name.
    mg.font = `600 14px ${UI}`;
    const nx = x + mg.measureText(e.name).width / 2 + 14;
    tag(mg, 'คุ้ม', nx, top - 2, `600 11px ${UI}`, '#9fe8b8');
  }
  armourBadge(mg, w, e, x, top - 2, (mg.font = `600 14px ${UI}`, mg.measureText(e.name).width)); // ตัวแดง/เหลือง plate (UX 10-11)
  stanceIcon(mg, e, x, Math.min(top - 44, (plate?.y ?? top) - 15));
}
